import { useCallback, useEffect, useRef } from "react";

/**
 * #1299 — after a Save that finds a missing or invalid field, take the player
 * to the first one: scroll it to the middle of the screen and focus it. On a
 * phone Save sits below the form, so without this the marks are off screen.
 *
 * The first element in DOM order inside `root` that is `aria-invalid="true"`,
 * or carries `data-field-invalid="true"` (for a control that cannot take
 * `aria-invalid`, such as a button that opens a picker). Returns the element,
 * or `null` and does nothing when nothing is invalid.
 */
let programmatic = false;

/**
 * True only while `focusFirstInvalid` is calling `.focus()`. A field's
 * `onFocus` reads it to tell the move from Save apart from the player's own
 * tap or Tab (#1299, D312). The focus event fires synchronously inside
 * `.focus()`, so the flag is never stale.
 */
export function isProgrammaticFocus() {
  return programmatic;
}

export function focusFirstInvalid(root) {
  if (!root || typeof root.querySelector !== "function") return null;
  const el = root.querySelector('[aria-invalid="true"], [data-field-invalid="true"]');
  if (!el) return null;
  // #1336: focus first, then scroll. Chromium ignores focus({ preventScroll })
  // on a date input: focus moves to its inner day/month sub-field, which scrolls
  // itself into view at once and cancels a smooth scroll that is already
  // running. When the field is just under the top edge Chromium decides no
  // scroll is needed, so nothing replaces it. Focusing before scrollIntoView
  // means our scroll is the last one asked for.
  programmatic = true;
  try {
    el.focus({ preventScroll: true });
  } finally {
    programmatic = false;
  }
  if (typeof el.scrollIntoView === "function") {
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    el.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
  }
  return el;
}

/**
 * Returns `arm()`. Call it where Save is pressed, before the handler sets the
 * errors. The next time `errors` changes, if any value is truthy, the first
 * invalid field is focused once. Without `arm()` nothing moves, so errors that
 * clear one by one while the player types never steal focus. A submit that
 * finds no error clears the flag.
 */
export function useFocusFirstInvalid(rootRef, errors) {
  const armed = useRef(false);
  const arm = useCallback(() => {
    armed.current = true;
  }, []);
  useEffect(() => {
    if (!armed.current) return;
    armed.current = false;
    if (errors && Object.values(errors).some(Boolean)) focusFirstInvalid(rootRef.current);
  }, [errors, rootRef]);
  return arm;
}
